import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, AlertCircle, Loader2, Ban, X, CheckCircle2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { getPaymentListBySource, adminCancelSubscription } from '../services/adminApi';

const SOURCE_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: 'isbulsun.com', label: 'isbulsun.com' },
  { value: 'career.sebnemramadan.com', label: 'career.sebnemramadan.com' },
];

const STATUS = {
  ACTIVE: { label: 'Aktif', color: '#00C9A7', bg: 'rgba(0,201,167,0.12)' },
  FAILED: { label: 'Başarısız', color: '#DC2626', bg: 'rgba(220,38,38,0.1)' },
  CANCELED: { label: 'İptal edildi', color: '#6B7280', bg: 'rgba(107,114,128,0.12)' },
  CANCELLED: { label: 'İptal edildi', color: '#6B7280', bg: 'rgba(107,114,128,0.12)' },
  PENDINGPAYMENT: { label: 'Beklemede', color: '#B45309', bg: 'rgba(245,158,11,0.14)' },
  PROCESSING: { label: 'Beklemede', color: '#B45309', bg: 'rgba(245,158,11,0.14)' },
};

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return value;
  }
}

function formatPrice(price, currency) {
  if (price === null || price === undefined) return '—';
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency || 'TRY' }).format(price);
  } catch {
    return `${price} ${currency || ''}`.trim();
  }
}

const AdminPaymentsPage = () => {
  const navigate = useNavigate();
  const [source, setSource] = useState('isbulsun.com');
  const [payments, setPayments] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // İptal/iade modalı
  const [cancelTarget, setCancelTarget] = useState(null);
  const [refundPayment, setRefundPayment] = useState(false);
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    getPaymentListBySource(source)
      .then((res) => {
        if (!res.ok || res.status === false) {
          setError(res.message || 'Ödeme listesi alınamadı.');
          setPayments([]);
          return;
        }
        setPayments(res.data || []);
      })
      .catch(() => {
        setError('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
        setPayments([]);
      })
      .finally(() => setLoading(false));
  }, [source]);

  useEffect(() => { load(); }, [load]);

  const openCancel = (p) => {
    setCancelTarget(p);
    setRefundPayment(false);
    setReason('');
    setCancelError('');
  };

  const closeCancel = () => {
    if (cancelling) return;
    setCancelTarget(null);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError('');
    try {
      const res = await adminCancelSubscription({
        id: cancelTarget.subscriptionId,
        refundPayment,
        reason: reason.trim() || undefined,
      });

      if (!res.ok || res.status === false) {
        setCancelError(res.message || 'İşlem gerçekleştirilemedi.');
        return;
      }

      setCancelTarget(null);
      setNotice(res.message || (refundPayment ? 'Abonelik iptal edildi ve iade denendi.' : 'Abonelik iptal edildi.'));
      load();
    } catch {
      setCancelError('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <AdminLayout title="Abonelikler">
      <div className="admin-toolbar">
        <select
          className="admin-select"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        >
          {SOURCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="admin-panel-card">
        {notice && !loading && (
          <div className="admin-info"><CheckCircle2 size={14} /> {notice}</div>
        )}

        {loading && (
          <div className="admin-placeholder">
            <Loader2 className="spin" />
            <h3>Yükleniyor...</h3>
          </div>
        )}

        {!loading && error && (
          <div className="admin-placeholder">
            <AlertCircle />
            <h3>Ödeme listesi alınamadı</h3>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && payments?.length === 0 && (
          <div className="admin-placeholder">
            <CreditCard />
            <h3>Ödeme kaydı yok</h3>
            <p>Seçili kaynak için henüz bir ödeme kaydı bulunmuyor.</p>
          </div>
        )}

        {!loading && !error && payments?.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Ad Soyad</th>
                  <th>E-posta / Telefon</th>
                  <th>Paket Adı</th>
                  <th>Fiyat</th>
                  <th>Durum</th>
                  <th>Tarih</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const statusKey = String(p.status || '').toUpperCase();
                  const statusInfo = STATUS[statusKey] || { label: p.status || '—', color: '#6B7280', bg: '#F3F4F8' };
                  const showFailReason = (statusKey === 'FAILED' || statusKey === 'CANCELED' || statusKey === 'CANCELLED') && p.failReason;

                  return (
                    <tr
                      key={p.subscriptionId || `${p.candidateId}-${p.planName}`}
                      className="admin-table-row--clickable"
                      onClick={() => p.candidateId && navigate(`/panel/adaylar/${p.candidateId}`)}
                    >
                      <td>{[p.candidateName, p.candidateSurname].filter(Boolean).join(' ') || '—'}</td>
                      <td>
                        <div>{p.candidateEmail || '—'}</div>
                        {p.candidatePhone && <div className="admin-table-subtext">{p.candidatePhone}</div>}
                      </td>
                      <td>{p.planName || '—'}</td>
                      <td>{formatPrice(p.price, p.currency)}</td>
                      <td>
                        <span className="admin-badge" style={{ color: statusInfo.color, background: statusInfo.bg }}>
                          {statusInfo.label}
                        </span>
                        {showFailReason && (
                          <div className="admin-table-subtext admin-table-subtext--error" title={p.failReason}>
                            {p.failReason}
                          </div>
                        )}
                      </td>
                      <td>{formatDate(p.startDate)}</td>
                      <td>
                        {statusKey === 'ACTIVE' && (
                          <button
                            type="button"
                            className="admin-table-action admin-table-action--danger"
                            onClick={(e) => { e.stopPropagation(); openCancel(p); }}
                          >
                            <Ban size={13} /> İptal/İade
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cancelTarget && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && closeCancel()}
        >
          <div className="admin-modal">
            <div className="admin-modal__header">
              <h3>Aboneliği İptal Et</h3>
              <button type="button" className="admin-modal__close" onClick={closeCancel} disabled={cancelling}>
                <X size={18} />
              </button>
            </div>

            <p className="admin-modal__text">
              <strong>{[cancelTarget.candidateName, cancelTarget.candidateSurname].filter(Boolean).join(' ') || 'Bu aday'}</strong>'ın{' '}
              <strong>{cancelTarget.planName || 'paketi'}</strong> aboneliği iptal edilecek. Bu işlem geri alınamaz.
            </p>

            <label className="admin-modal__checkbox">
              <input
                type="checkbox"
                checked={refundPayment}
                onChange={(e) => setRefundPayment(e.target.checked)}
              />
              <span>Iyzico üzerinden gerçek para iadesi de dene (son başarılı ödeme)</span>
            </label>

            <div className="admin-form-field">
              <label htmlFor="cancel-reason">Not (opsiyonel)</label>
              <textarea
                id="cancel-reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="İptal gerekçesi..."
              />
            </div>

            {cancelError && <div className="admin-error">{cancelError}</div>}

            <div className="admin-modal__actions">
              <button type="button" className="admin-submit" onClick={handleConfirmCancel} disabled={cancelling}>
                {cancelling ? <Loader2 size={16} className="spin" /> : <Ban size={15} />}
                {cancelling ? 'İşleniyor...' : refundPayment ? 'İptal Et ve İade Dene' : 'Aboneliği İptal Et'}
              </button>
              <button type="button" className="admin-modal__secondary" onClick={closeCancel} disabled={cancelling}>
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPaymentsPage;
